import React from 'react'
import { PrimeIcons } from 'primereact/api'

import Text from '../../../ui/Text'

export type SubscriptionMarketingFeature = {
    label: string
    isActive: boolean
    productLevel: number
}

export type SubscriptionCardTag = {
    content: React.ReactNode
    className?: string
}

export type SubscriptionCardLayoutProps = {
    testId?: string
    title: React.ReactNode
    price: string
    priceSuffix?: string
    priceDetailLines: string[]
    description?: React.ReactNode
    headerContent?: React.ReactNode
    isRecommended?: boolean
    discountInPercent?: number
    tag?: SubscriptionCardTag
    marketingFeatures: SubscriptionMarketingFeature[]
    footer: React.ReactNode
}

export function SubscriptionCardLayout({
    testId,
    title,
    description,
    headerContent,
    price,
    priceSuffix,
    priceDetailLines,
    isRecommended = false,
    discountInPercent,
    tag,
    marketingFeatures,
    footer,
}: SubscriptionCardLayoutProps) {
    return (
        <div
            data-testid={testId}
            className="flex flex-column h-full surface-border overflow-hidden shadow-1 font-light border-1 gap-5 relative"
        >
            <div className={isRecommended ? 'bg-green-100' : 'bg-gray-200'} style={{ height: '7px' }} />

            <div className="px-6 h-9rem">
                {tag ? (
                    <div
                        data-testid={`subscription-${title}-tag`}
                        className={`absolute top-0 right-0 px-3 mt-3 mr-3 py-1 text-sm border-round-sm text-center ${tag.className ?? 'bg-gray-200'} text-900`}
                    >
                        {tag.content}
                    </div>
                ) : null}
                <h3 className="text-2xl font-bold">{title}</h3>

                {description ? <p className="text-base">{description}</p> : null}

                {headerContent}
            </div>

            <div className="text-3xl h-12rem justify-content-center flex flex-column bg-green-100 relative">
                {discountInPercent ? (
                    <div
                        data-testid={`subscription-${title}-discount`}
                        className="absolute top-0 right-0 px-3 mt-3 mr-3 py-1 text-sm border-round-sm text-center bg-green-300"
                    >
                        -{discountInPercent}%
                    </div>
                ) : null}
                <div className="relative px-6 flex flex-column gap-3">
                    <p className="text-large m-0">
                        {price} <span className="text-green-400">{priceSuffix}</span>
                    </p>

                    {priceDetailLines.map((priceDetailLine) => (
                        <p className="text-base m-0" key={priceDetailLine}>
                            {priceDetailLine}
                        </p>
                    ))}
                </div>
            </div>

            {marketingFeatures.length > 0 ? (
                <div className="px-6">
                    <ul className="m-0 p-0 list-none flex flex-column gap-3">
                        {marketingFeatures.map((marketingFeature) => (
                            <li
                                key={marketingFeature.label}
                                className={`flex align-items-center ${marketingFeature.isActive ? 'text-900' : 'text-500'}`}
                            >
                                <i
                                    aria-hidden="true"
                                    className={`mr-2 ${marketingFeature.isActive ? PrimeIcons.CHECK : PrimeIcons.MINUS}`}
                                />
                                <span>
                                    <Text code={marketingFeature.label} />
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}

            <div className="px-6 h-5rem pb-4 flex align-items-center justify-content-center">{footer}</div>
        </div>
    )
}
